import * as React from "react";
import {
    Box,
    Button,
    ButtonProps,
    IconButton,
    IconButtonProps,
    Link,
    LinkProps,
    SvgIcon,
    SvgIconProps,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
    useTheme,
} from "@mui/material";
import { HiOutlineLightBulb } from "react-icons/hi";
import { observer } from "mobx-react-lite";
import { useAppServices } from "../app/services";
import { MdOutlineInfo } from "react-icons/md";
import { Theme, styled } from "@mui/material/styles";
import { getIsPrivateEdition } from "../auth/PrivateEditionView";
import { useCallback } from "react";
import { useOpenHelpArticle, useOpenHelpSearch } from "./hooks/help_hooks";
import { BlackTooltip } from "../../common/tooltip/ColorTooltip";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";

export const HELP_CENTER_URL = `${import.meta.env.VITE_CUSTOMER_CENTER_URL}/cdc`;

export const getCustomerCenterAuthCallbackUrl = (redirect?: string) => {
    let u = `${HELP_CENTER_URL}/auth-callback?from-app=cirrus-data-cloud`;
    if (!!redirect) {
        u += "&redirect=" + encodeURIComponent(redirect);
    }
    return u;
};
export const HELP_CENTER_EXTERNAL_URL = "https://customer.cirrusdata.com/cdc";
export const TERMS_OF_SERVICE_URL = `${import.meta.env.VITE_CUSTOMER_CENTER_URL}/cdc/kb/articles/terms-and-conditions-Yn9anyBaw1`;
export const PRIVACY_POLICY_URL = `${import.meta.env.VITE_CUSTOMER_CENTER_URL}/cdc/kb/articles/privacy-policy-owKpQxHGIz`;
export const EULA_URL = `${import.meta.env.VITE_CUSTOMER_CENTER_URL}/cdc/kb/articles/end-user-license-agreement-JfR3AybrSo`;
export const API_DOCS_ROUTE = "/apidocs";

export enum AppHintID {
    DEPLOY_CIRRUS_MIGRATE = "deploy cirrus migrate",
    CREATE_H2H_LINK_FORM = "h2h",
    LICENSE_KEY = "license key",
    CMC_RELAY = "cdc relay",
    H2H_RELAY = "h2h relay",
}

export enum KnownArticle {
    CMC_OS_REQUIREMENTS = "o3F2UdKnds",
    CMC_RE_IP = "RehAUTx6wW",
    CMC_HOST_NETWORK_REQUIREMENTS = "fTgWNObJSe",
    CMC_LICENSING = "T4Y4Oy5pfj",
    CMC_TESTED_LIMITS = "3hCPGEm71W",
    CMC_COMPUTE_MIGRATION = "rYiH6fi8PR",
    MIGRATING_CLUSTER_VOLUMES = "vwcKXMHiKS",
    MIGRATING_BOOT_VOLUMES = "nYNrq8d0MO",
    CMC_HELPER_VMWARE = "kAH0QPZVtq",
    TRANSFER_LICENSE_CREDITS = "b2c7hcIbQR",
    PROJECT_MEMBER_ROLES = "07bfahosUn",
    MIGRATION_SESSION_IN_ERROR = "n5Vb9Vvnte",
    REDEEM_LICENSE = "jEZzAVwoR3",
    ABOUT_PRIVATE_EDITION = "982y1OGjIA",
    PRIVATE_EDITION_LICENSING = "HuqZNugAaN",
    INSTALLING_PRIVATE_EDITION = "YFeUg9fVMw",
    MIGRATEOPS_CONFIG = "QeP0iBnBE2",
    MIGRATEOPS = "hCCHcmhfbj",
    MIGRATING_CLUSTERS = "vwcKXMHiKS",
    MIGRATION_CMOTION = "KfnUp6wVXO",
    MIGRATION_THROTTLING = "mQ5NE9CmBM",
    H2H_RELAY = "z8PCGGYce4",
    MCP_SERVER_SETUP = "nDgzMUqB67",
    API_ACCESS = "vxkD6nWyT0",
}

export const navigateToHelpCenter = (articleSlug?: string, type?: "search" | "article") => {
    window.open(`${HELP_CENTER_URL}/kb/${!!articleSlug ? (type === "article" ? `articles/${articleSlug}` : `search?q=${articleSlug}`) : ""}`, "_blank");
};

export const getExternalLinkConfirmDialog = (link: string) => {
    return {
        title: appI18n.t("common:links.externalContent"),
        message: (
            <>
                <Typography>{appI18n.t("common:links.externalDescription")}</Typography>
                <Typography>{appI18n.t("common:links.externalOfflineHelp")}</Typography>
                <br />
                {link}
            </>
        ),
        autoConfirmationQuestionLine: false,
        okButtonLabel: appI18n.t("common:actions.openInBrowser"),
        cancelButtonLabel: appI18n.t("common:actions.close"),
    };
};

export const useNavigateToExternalLink = (link: string) => {
    const { dialogService } = useAppServices();
    const isPrivateEdition = getIsPrivateEdition();

    const navFunc = () => window.open(link, "_blank");

    if (isPrivateEdition) {
        return async () => {
            const confirmed = await dialogService.addConfirmDialog(getExternalLinkConfirmDialog(link));
            if (confirmed) {
                navFunc();
            }
        };
    } else {
        return navFunc;
    }
};

export const useNavigateToHelpCenterKb = () => {
    return useNavigateToExternalLink(`${HELP_CENTER_URL}/kb`);
};

interface NavLinkProps {
    label?: string;
}

export const EULALink: React.FC<NavLinkProps & Partial<LinkProps>> = (props) => {
    const nav = useNavigateToExternalLink(EULA_URL);
    const { t } = useTranslation();
    return (
        <Link onClick={nav} {...props}>
            {props.label || t("common:legal.eula")}
        </Link>
    );
};

export const TermsOfServiceLink: React.FC<NavLinkProps & Partial<LinkProps>> = (props) => {
    const nav = useNavigateToExternalLink(TERMS_OF_SERVICE_URL);
    const { t } = useTranslation();

    return (
        <Link onClick={nav} {...props}>
            {props.label || t("common:legal.termsOfService")}
        </Link>
    );
};

export const PrivacyPolicyLink: React.FC<NavLinkProps & Partial<LinkProps>> = (props) => {
    const nav = useNavigateToExternalLink(PRIVACY_POLICY_URL);
    const { t } = useTranslation();

    return (
        <Link onClick={nav} {...props}>
            {props.label || t("common:legal.privacyPolicy")}
        </Link>
    );
};

export const HintIcon: React.FC<Partial<SvgIconProps>> = (p) => {
    return (
        <SvgIcon {...p}>
            <HiOutlineLightBulb />
        </SvgIcon>
    );
};

export const ApiLink: React.FC<NavLinkProps & Partial<LinkProps>> = (props) => {
    const nav = useNavigateToExternalLink(API_DOCS_ROUTE);
    const { t } = useTranslation();

    return (
        <Link onClick={nav} {...props}>
            {props.label || t("common:legal.api")}
        </Link>
    );
};

export const HelpCenterLink: React.FC<NavLinkProps & Partial<LinkProps>> = (props) => {
    const nav = useNavigateToExternalLink(HELP_CENTER_URL);
    const { t } = useTranslation();

    return (
        <Link onClick={nav} {...props}>
            {props.label || t("common:legal.helpCenter")}
        </Link>
    );
};

interface HintButtonProps {
    hintID?: AppHintID;
    articleID?: KnownArticle;
    buttonProps?: Partial<IconButtonProps>;
    icon?: React.ReactNode;
    tooltipTitle?: string;
}

export const HintButton: React.FC<HintButtonProps> = observer((p) => {
    const openHelpArticle = useOpenHelpArticle();
    const openHelpSearch = useOpenHelpSearch();
    const { t } = useTranslation();

    const handleClick = useCallback(async () => {
        if (!!p.hintID) {
            await openHelpSearch(p.hintID);
        } else if (!!p.articleID) {
            await openHelpArticle(p.articleID);
        }
    }, [p.hintID, p.articleID, openHelpArticle, openHelpSearch]);
    return (
        <Tooltip title={p.tooltipTitle ?? t("common:links.hint")}>
            <IconButton color={"default"} {...p.buttonProps} onClick={handleClick}>
                {p.icon ?? <HintIcon />}
            </IconButton>
        </Tooltip>
    );
});

// ======================
// LearnMoreButton
// ======================

interface LearnMoreButtonProps {
    hintID?: AppHintID;
    articleID?: KnownArticle;
    buttonText?: string;
}

export const LearnMoreButton: React.FC<LearnMoreButtonProps & Partial<ButtonProps>> = (p) => {
    const openHelpArticle = useOpenHelpArticle();
    const openHelpSearch = useOpenHelpSearch();
    const { hintID, articleID, buttonText, ...buttonProps } = p;
    const { t } = useTranslation();

    const handleClick = useCallback(async () => {
        if (!!p.hintID) {
            await openHelpSearch(p.hintID);
        } else if (!!p.articleID) {
            await openHelpArticle(p.articleID);
        }
    }, [p.hintID, p.articleID, openHelpArticle, openHelpSearch]);
    return (
        <Button onClick={handleClick} {...buttonProps}>
            {p.buttonText || t("common:links.learnMore")}
        </Button>
    );
};

interface LearnMoreLinkProps {
    articleID: KnownArticle;
    hintID?: AppHintID;
    linkText?: string;
}

export const LearnMoreLink: React.FC<LearnMoreLinkProps & Partial<LinkProps>> = (p) => {
    const { articleID, hintID, linkText, ...linkProps } = p;
    const openHelpArticle = useOpenHelpArticle();
    const openHelpSearch = useOpenHelpSearch();
    const { t } = useTranslation();

    const handleClick = useCallback(async () => {
        if (!!p.hintID) {
            await openHelpSearch(p.hintID);
        } else if (!!p.articleID) {
            await openHelpArticle(p.articleID);
        }
    }, [p.hintID, p.articleID, openHelpArticle, openHelpSearch]);

    return (
        <Link onClick={handleClick} {...linkProps}>
            {p.linkText || t("common:links.learnMore")}
        </Link>
    );
};

// ======================
// QuickTipButton
// ======================

interface QuickTipButtonProps {
    disabled?: boolean;
    tooltipColor?: "black" | "gray" | "primary";
    iconSize?: number;
    icon?: "info" | "help";
}

export const QuickTipButton: React.FC<QuickTipButtonProps & Partial<TooltipProps>> = observer((p) => {
    const { disabled, tooltipColor, iconSize, icon, ...props } = p;
    const theme = useTheme();

    if (disabled) {
        return <MdOutlineInfo size={iconSize} opacity={0.2} color={theme.palette.primary.main} />;
    }

    if (tooltipColor === "black") {
        return (
            <BlackTooltip title={p.title} arrow {...props}>
                <Box>
                    {icon === "help" ? (
                        <AiOutlineQuestionCircle size={iconSize} color={theme.palette.primary.main} />
                    ) : (
                        <MdOutlineInfo size={iconSize} color={theme.palette.primary.main} />
                    )}
                </Box>
            </BlackTooltip>
        );
    }
    if (tooltipColor === "gray") {
        return (
            <Tooltip title={p.title} arrow {...props}>
                {icon === "help" ? (
                    <AiOutlineQuestionCircle size={iconSize} color={theme.palette.primary.main} />
                ) : (
                    <MdOutlineInfo size={iconSize} color={theme.palette.primary.main} />
                )}
            </Tooltip>
        );
    }
    return (
        <BlueTooltip title={p.title} arrow {...props}>
            <SvgIcon sx={{ color: (t: Theme) => t.palette.primary.main }}>
                {icon === "help" ? (
                    <AiOutlineQuestionCircle size={iconSize} color={theme.palette.primary.main} />
                ) : (
                    <MdOutlineInfo size={iconSize} color={theme.palette.primary.main} />
                )}
            </SvgIcon>
        </BlueTooltip>
    );
});

const BlueTooltip: React.FC<TooltipProps> = styled(<TooltipProps,>({ ...props }) => (
    <Tooltip classes={{ popper: props.className }} children={props.children} title={props.title} {...props} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        boxShadow: theme.shadows[1],
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.primary.main,
    },
}));
